/**
 * Redacts personal and claim data from Sentry payloads before they leave the
 * process. Used by the server (server/instrument.ts, which the worker also
 * loads) and the client (client/src/main.tsx) as beforeSend,
 * beforeSendTransaction, beforeBreadcrumb and beforeSendLog.
 *
 * Two layers:
 * - Keys: a value is replaced when its key names personal, claim, payment or
 *   auth data (email, phone, names, addresses, policy and claim numbers, SSN,
 *   DOB, bank and card details, auth headers, cookies, tokens, free-text notes).
 * - Text: in every other string, objects printed as JSON or by util.inspect
 *   are scrubbed by key too, and emails, phone numbers, SSNs, card numbers,
 *   bearer tokens, JWTs, URL credentials and sensitive query parameters are
 *   replaced. Names, addresses and claim numbers written into a sentence are
 *   not detected.
 *
 * The functions never mutate their input: console breadcrumbs carry the app's
 * own objects.
 *
 * shared/piiConfig.ts is not reused: its field hints ("claim", "key",
 * "account") would also drop the ids an error report needs.
 */

export const REDACTED = '[Filtered]';

const MAX_DEPTH = 10;

// Whole words of a key: "contactEmail" -> contact, email; "x-auth-token" -> x, auth, token.
const SENSITIVE_WORDS = new Set([
  // contact
  'email', 'emails', 'mail', 'phone', 'phones', 'telephone', 'tel', 'mobile', 'cell', 'cellphone', 'fax',
  // address parts and location
  'street', 'street1', 'street2', 'line1', 'line2', 'city', 'zip', 'zipcode', 'postal', 'postalcode', 'postcode',
  'lat', 'lng', 'lon', 'latitude', 'longitude', 'coordinates', 'location', 'ip', 'forwarded',
  // identity
  'ssn', 'ein', 'tin', 'dob', 'birthdate', 'birthday', 'passport',
  // bank and payment ("bankName" is caught as a name, "bankAccount" by the fragments)
  'iban', 'swift', 'bic', 'cvv', 'cvc',
  // auth
  'password', 'passwd', 'pwd', 'passphrase', 'secret', 'token', 'tokens', 'cookie', 'cookies', 'authorization',
  'auth', 'jwt', 'bearer', 'otp', 'pin', 'credential', 'credentials', 'signature', 'dsn', 'sid', 'csrf', 'xsrf',
  'apikey',
  // search terms are often names or addresses
  'search', 'q', 'query', 'term', 'keyword', 'keywords',
]);

// Substrings of the joined key, for keys written without separators ("phonenumber").
const SENSITIVE_FRAGMENTS = [
  'email', 'phone', 'password', 'passwd', 'secret', 'token', 'apikey', 'accesskey', 'privatekey',
  'authorization', 'cookie', 'credential', 'csrf', 'xsrf',
  'policynum', 'policyno', 'claimnum', 'claimno', 'loannum', 'accountnum', 'acctnum', 'routingnum', 'cardnum',
  'creditcard', 'debitcard', 'checknum', 'licensenum', 'driverslicense', 'bankaccount',
  'socialsecurity', 'taxid', 'dateofbirth', 'birth', 'pointofcontact',
];

// Last word of a key that holds free text about the claim ("policyholderNotes", "evidenceText")
// or a person ("pointOfContact", "insured"). As a last word only, so "contactId" is kept.
const LAST_WORD_KEYS = new Set([
  'note', 'notes', 'comment', 'comments', 'body', 'html', 'text', 'content', 'subject', 'transcript',
  'description', 'lossdescription',
  'policyholder', 'insured', 'claimant', 'homeowner', 'contact',
]);

// "<qualifier>name" / "<qualifier>address" that name a system thing, not a person or a place.
const TECHNICAL_QUALIFIERS = new Set([
  'host', 'server', 'path', 'type', 'class', 'component', 'route', 'event', 'job', 'queue', 'workflow',
  'scheduler', 'model', 'provider', 'service', 'table', 'field', 'column', 'integration', 'transaction', 'span',
  'op', 'operation', 'function', 'method', 'module', 'env', 'bucket', 'browser', 'os', 'runtime', 'app',
  'release', 'step', 'tool', 'prompt', 'metric', 'feature', 'flag', 'tag', 'header', 'param', 'attribute', 'sdk',
  'error', 'exception', 'schema', 'index', 'key', 'node', 'project', 'db', 'database', 'domain', 'region',
  'zone', 'template', 'peer', 'system', 'destination', 'executable',
]);

// A number under a key ending in one of these is a count ("promptTokens": 1200), not a secret.
const COUNT_WORDS = new Set(['tokens', 'count', 'size', 'length', 'limit']);

// Query parameters that carry OAuth secrets (they are harmless as body keys).
const QUERY_ONLY_KEYS = new Set(['code', 'state']);

// Keys whose value is a raw query string ("a=1&b=2").
const QUERY_STRING_KEYS = new Set(['querystring', 'httpquery', 'urlquery']);

// Sentry SDK contexts that describe the runtime, never the user.
const SDK_CONTEXTS = new Set(['os', 'browser', 'runtime', 'device', 'app', 'culture', 'cloud_resource']);

function keyWords(key: string): string[] {
  return key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
}

function namesPersonOrPlace(words: string[]): boolean {
  for (let i = 0; i < words.length; i++) {
    const word = words[i].replace(/\d+$/, ''); // "address2" -> "address"
    const suffix = ['addresses', 'address', 'names', 'name'].find((s) => word.endsWith(s));
    if (!suffix) continue;
    const qualifier = word === suffix ? words[i - 1] : word.slice(0, -suffix.length);
    if (!qualifier || !TECHNICAL_QUALIFIERS.has(qualifier)) return true;
  }
  return false;
}

/** True when the value stored under `key` must not reach Sentry. */
export function isSensitiveKey(key: string): boolean {
  const words = keyWords(key);
  if (words.length === 0) return false;
  const joined = words.join('');
  return (
    LAST_WORD_KEYS.has(words[words.length - 1]) ||
    words.some((word) => SENSITIVE_WORDS.has(word)) ||
    SENSITIVE_FRAGMENTS.some((fragment) => joined.includes(fragment)) ||
    namesPersonOrPlace(words)
  );
}

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value.replace(/\+/g, ' '));
  } catch {
    return value;
  }
}

const QUERY_PARAM = /([?&#;])([^=&#;\s?]+)=([^&#;\s]*)/g;
const URL_CREDENTIALS = /(\/\/)[^/\s:@]+:[^/\s@]+@/g;
const BEARER = /\b(Bearer|Basic)\s+[A-Za-z0-9._~+/=-]+/gi;
const JWT = /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g;
const EMAIL = /[A-Za-z0-9._%+-]+(?:@|%40)[A-Za-z0-9.-]+\.[A-Za-z]{2,}/gi;
const SSN = /\b\d{3}-\d{2}-\d{4}\b/g;
const CARD = /\b(?:\d{4}[ -]){3}\d{1,7}\b/g;
const PHONE = /(?:\+\d{1,3}[\s.-]?)?(?:\(\d{3}\)\s?|\b\d{3}[\s.-])\d{3}[\s.-]\d{4}\b/g;
const E164_PHONE = /\+\d{10,15}\b/g;
// Objects printed into text: JSON ("key": value) and Node's util.inspect (key: 'value').
const QUOTED_KEY_VALUE = /"([^"\\\n]{1,100})"(\s*:\s*)("(?:[^"\\\n]|\\.)*"|-?\d[\d.eE+-]{0,30})/g;
const BARE_KEY_VALUE = /\b([A-Za-z_$][\w$-]{0,100})(\s*:\s*)('(?:[^'\\\n]|\\.)*'|"(?:[^"\\\n]|\\.)*"|-?\d[\d.eE+-]{0,30})/g;

// A number under a count key ("promptTokens": 1200) is kept, like in scrubValue.
function isSensitivePair(key: string, value: string): boolean {
  if (!isSensitiveKey(key)) return false;
  const words = keyWords(key);
  return !(/^-?\d/.test(value) && COUNT_WORDS.has(words[words.length - 1]));
}

/**
 * Log lines and console messages carry objects as JSON ("Form data: {...}",
 * server/logger.ts appends JSON.stringify(data)). Scrubs a JSON object or array
 * that ends the text by key; returns the text unchanged when there is none.
 */
function scrubTrailingJson(text: string): string {
  const trimmed = text.trimEnd();
  const last = trimmed[trimmed.length - 1];
  if (last !== '}' && last !== ']') return text;
  for (const opener of ['{', '[']) {
    const start = trimmed.indexOf(opener);
    if (start === -1) continue;
    try {
      return text.slice(0, start) + JSON.stringify(scrubValue(JSON.parse(trimmed.slice(start))));
    } catch {
      // Not JSON from this opener (e.g. a "[INFO]" prefix): try the next one.
    }
  }
  return text;
}

/** Redacts sensitive query parameters and personal patterns inside free text or a URL. */
export function redactText(text: string): string {
  return scrubTrailingJson(text)
    .replace(QUOTED_KEY_VALUE, (match, key: string, colon: string, value: string) =>
      isSensitivePair(key, value) ? `"${key}"${colon}"${REDACTED}"` : match,
    )
    .replace(BARE_KEY_VALUE, (match, key: string, colon: string, value: string) => {
      if (!isSensitivePair(key, value)) return match;
      const quote = value.startsWith('"') ? '"' : "'";
      return `${key}${colon}${quote}${REDACTED}${quote}`;
    })
    .replace(QUERY_PARAM, (match, sep: string, name: string) => {
      const decoded = safeDecode(name);
      return isSensitiveKey(decoded) || QUERY_ONLY_KEYS.has(decoded.toLowerCase())
        ? `${sep}${name}=${REDACTED}`
        : match;
    })
    .replace(URL_CREDENTIALS, `$1${REDACTED}@`)
    .replace(BEARER, `$1 ${REDACTED}`)
    .replace(JWT, REDACTED)
    .replace(EMAIL, REDACTED)
    .replace(SSN, REDACTED)
    .replace(CARD, REDACTED)
    .replace(PHONE, REDACTED)
    .replace(E164_PHONE, REDACTED);
}

/** Redacts a raw query string ("email=a%40b.test&status=open"), with or without the leading "?". */
function redactQueryString(query: string): string {
  return query.startsWith('?') ? redactText(query) : redactText(`?${query}`).slice(1);
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object') return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

/**
 * Returns a scrubbed copy of `value`. A value under a sensitive key is replaced
 * (booleans, null and undefined are kept: they say nothing about a person).
 */
function scrubValue(value: unknown, key?: string, depth = 0): unknown {
  if (value === null || value === undefined || typeof value === 'boolean') return value;
  if (key !== undefined) {
    const words = keyWords(key);
    if (typeof value === 'string' && QUERY_STRING_KEYS.has(words.join(''))) return redactQueryString(value);
    if (typeof value === 'number' && COUNT_WORDS.has(words[words.length - 1])) return value;
    if (isSensitiveKey(key)) return REDACTED;
  }
  if (typeof value === 'string') return redactText(value);
  if (depth >= MAX_DEPTH) return REDACTED;
  if (Array.isArray(value)) return value.map((item) => scrubValue(item, undefined, depth + 1));
  if (isPlainObject(value)) {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) out[k] = scrubValue(v, k, depth + 1);
    return out;
  }
  return value;
}

const FORM_BODY = /^[^\s=&]+=[^\s&]*(?:&[^\s=&]+=[^\s&]*)*$/;

/**
 * A string body is kept only as JSON or a urlencoded form, scrubbed by key.
 * Anything else (JSON the SDK cut off at 10 KB with "...", multipart, plain
 * text) cannot be scrubbed by key, so it is dropped.
 */
function scrubRequestBody(data: unknown): unknown {
  if (typeof data !== 'string') return scrubValue(data);
  const trimmed = data.trim();
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      return JSON.stringify(scrubValue(JSON.parse(trimmed)));
    } catch {
      return REDACTED;
    }
  }
  return FORM_BODY.test(trimmed) ? redactQueryString(trimmed) : REDACTED;
}

function scrubQueryString(query: unknown): unknown {
  if (typeof query === 'string') return redactQueryString(query);
  if (Array.isArray(query)) {
    return query.map((pair) =>
      Array.isArray(pair) && typeof pair[0] === 'string'
        ? [pair[0], scrubValue(pair[1], pair[0])]
        : scrubValue(pair),
    );
  }
  return scrubValue(query);
}

function scrubRequest(request: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = { ...request };
  if (typeof out.url === 'string') out.url = redactText(out.url);
  if (out.query_string !== undefined) out.query_string = scrubQueryString(out.query_string);
  if (out.data !== undefined) out.data = scrubRequestBody(out.data);
  if (out.headers !== undefined) out.headers = scrubValue(out.headers);
  if (out.cookies !== undefined) out.cookies = REDACTED;
  if (out.env !== undefined) out.env = scrubValue(out.env);
  return out;
}

function scrubContexts(contexts: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [name, context] of Object.entries(contexts)) {
    out[name] = SDK_CONTEXTS.has(name) ? context : scrubValue(context);
  }
  return out;
}

function scrubException(exception: Record<string, unknown>): Record<string, unknown> {
  if (!Array.isArray(exception.values)) return exception;
  return {
    ...exception,
    values: exception.values.map((entry) => {
      if (!isPlainObject(entry)) return entry;
      const out: Record<string, unknown> = { ...entry };
      if (typeof out.value === 'string') out.value = redactText(out.value);
      const stacktrace = out.stacktrace;
      if (isPlainObject(stacktrace) && Array.isArray(stacktrace.frames)) {
        out.stacktrace = {
          ...stacktrace,
          frames: stacktrace.frames.map((frame) =>
            isPlainObject(frame) && frame.vars !== undefined ? { ...frame, vars: scrubValue(frame.vars) } : frame,
          ),
        };
      }
      return out;
    }),
  };
}

function scrubSpan(span: unknown): unknown {
  if (!isPlainObject(span)) return span;
  const out: Record<string, unknown> = { ...span };
  if (typeof out.description === 'string') out.description = redactText(out.description);
  if (out.data !== undefined) out.data = scrubValue(out.data);
  return out;
}

/** Breadcrumb scrubber (beforeBreadcrumb); also applied to the breadcrumbs on each event. */
export function scrubSentryBreadcrumb<B extends object>(breadcrumb: B): B {
  const out = { ...breadcrumb } as Record<string, unknown>;
  if (typeof out.message === 'string') out.message = redactText(out.message);
  if (out.data !== undefined) out.data = scrubValue(out.data);
  return out as B;
}

/** Error and transaction event scrubber (beforeSend, beforeSendTransaction). */
export function scrubSentryEvent<E extends object>(event: E): E {
  const out = { ...event } as Record<string, unknown>;
  if (typeof out.message === 'string') out.message = redactText(out.message);
  if (isPlainObject(out.logentry)) out.logentry = scrubValue(out.logentry);
  if (typeof out.transaction === 'string') out.transaction = redactText(out.transaction);
  if (isPlainObject(out.request)) out.request = scrubRequest(out.request);
  if (out.extra !== undefined) out.extra = scrubValue(out.extra);
  if (out.tags !== undefined) out.tags = scrubValue(out.tags);
  if (isPlainObject(out.contexts)) out.contexts = scrubContexts(out.contexts);
  if (isPlainObject(out.exception)) out.exception = scrubException(out.exception);
  if (Array.isArray(out.breadcrumbs)) {
    out.breadcrumbs = out.breadcrumbs.map((crumb) => (isPlainObject(crumb) ? scrubSentryBreadcrumb(crumb) : crumb));
  }
  if (Array.isArray(out.spans)) out.spans = out.spans.map(scrubSpan);
  if (isPlainObject(out.user)) {
    // Only the internal user id is kept: no email, username, name or IP address.
    const id = out.user.id;
    if (id === undefined || id === null) delete out.user;
    else out.user = { id };
  }
  return out as E;
}

/** Structured log scrubber (beforeSendLog). */
export function scrubSentryLog<L extends object>(log: L): L {
  const out = { ...log } as Record<string, unknown>;
  if (typeof out.message === 'string') out.message = redactText(out.message);
  if (out.attributes !== undefined) out.attributes = scrubValue(out.attributes);
  return out as L;
}
